import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import { contactRoles, entities, errors, fields, personObj, OTHER_PLACEHOLDER } from '../../../constants';
import { getLastAuthUser, personEntityTitle } from '../../../utils/helper';
import { isDesignatedContactComplete } from '../../../utils/completes';
import { isEmail, isPhoneWithCountry } from '../../../utils/validations';
import { ProfileContext } from '../../../context/Profile.context';
import { affilations } from '../../../constants/companyAffilations';
import { Lock, Trash2, User, UserCheck } from 'lucide-react';
import Controls from '../../Controls';
import { PhoneInputField } from '../../PhoneInputField';
import PrivateInformation from '../../PrivateInformation/PrivateInformation';
import PreviousAdded from '../../PreviousAdded';
import RemovePerson from '../../RemovePerson';
import { Box, Button, Dialog, DialogContent, DialogTitle, EntityLabel, EntityLabelGroup, SectionTitle, SelectField, TextField, Typography, Label, InfoPanel } from '@otcmarketsgroup/otcm-ui-lib';

const CONTACT_PARAM = fields.AUTH_USER;
const PREV_ADDED_VIEW = 'previousAdded';
const FORM_VIEW = 'formView';
const REMOVE_PERSON_VIEW = 'removePerson';

const AuthorizedUsersTab = ({ onPersonAdd, onPersonUpdate, onPersonRemove }) => {
  const [profileState] = useContext(ProfileContext);
  const [dialog, setDialog] = useState(false);
  const [contactView, setContactView] = useState(personObj);
  const [viewHistory, setViewHistory] = useState(null);
  const [validationError, setValidationError] = useState(false);
  const currentData = profileState.currentProfile;
  const companyName = profileState.profile.companyInfo.companyName;
  const peopleList = profileState.profile.peopleList;
  const authorizedUserList = peopleList.filter(person => person[CONTACT_PARAM]);
  const prevAddedList = peopleList.filter(person => !person[CONTACT_PARAM] && (person.isOfficer || person.isDirector || person.isPrimaryContact || person.isPrimaryBillingContact || person.isOtherBillingContact));
  const previousAddView = !contactView.id && prevAddedList && prevAddedList.length > 0;
  const contactTotal = peopleList.filter(person => person[CONTACT_PARAM]).length;
  const [slideView, setSlideView] = useState(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
  const showBGCheckLabel = currentData && currentData.peopleList && !currentData.peopleList.find(person => contactView.id && person.id === contactView.id);
  const isExistingContact = contactView && (contactView.isOfficer || contactView.isDirector);
  const isAdd = !contactView.id || (contactView && !contactView[CONTACT_PARAM]);
  const removeWarningText = ` Are you sure you want to remove this person from ${companyName}? Please note that without any Authorized IQ users, ${companyName} will not be able to make any updates via OTCIQ.`;
  const isReadOnly = profileState.readOnly;
  const isRemoveView = slideView === REMOVE_PERSON_VIEW;
  const dialogTitle = isRemoveView ? `Remove - ${personEntityTitle(contactView, false)}` : isAdd ? 'Add OTCIQ User' : personEntityTitle(contactView, false);

  useEffect(() => {
    if (!dialog) {
      setContactView(personObj);
      setSlideView(previousAddView ? PREV_ADDED_VIEW : FORM_VIEW);
      setValidationError(false);
    }
  }, [dialog, previousAddView]);

  const onFieldChange = (field, value) => {
    setContactView({
      ...contactView,
      [field]: value
    });
  };

  const onPhoneValueChanged = (phone, data) => {
    setContactView({
      ...contactView,
      phone,
      phoneCountryCode: data?.countryCode ? data.countryCode.toUpperCase() : contactView?.phoneCountryCode
    });
  };

  const onAffiliationChange = value => {
    setContactView({
      ...contactView,
      title1: value === OTHER_PLACEHOLDER ? OTHER_PLACEHOLDER : value
    });
  };

  const handleEdit = person => {
    setSlideView(FORM_VIEW);
    setContactView({ ...person });
    setValidationError(!isDesignatedContactComplete(person, CONTACT_PARAM));
    setDialog(true);
  };

  const handleCancelRemove = () => {
    setDialog(false);
  };

  const handleRemove = person => {
    onPersonRemove(person, CONTACT_PARAM);
  };

  const handleRemoveCustom = person => {
    setContactView(person);
    setSlideView(REMOVE_PERSON_VIEW);
    setDialog(true);
  };

  const isCustomRemove = person => {
    const totalRoles = contactRoles.filter(role => person[role]);

    return totalRoles.length > 1;
  };

  const onRemoveSubmit = person => {
    onPersonUpdate(person);
    setDialog(false);
  };

  const validateComplete = person => {
    return isDesignatedContactComplete(person, CONTACT_PARAM);
  };

  const handleCancelView = () => {
    viewHistory === PREV_ADDED_VIEW && prevAddedList && prevAddedList.length > 0 ? setSlideView(PREV_ADDED_VIEW) : setDialog(false);
    setContactView(personObj);
  };

  const handleSubmitPerson = () => {
    setValidationError(false);

    const contact = contactView;
    const onSubmit = !contact.id ? onPersonAdd : onPersonUpdate;

    contact[CONTACT_PARAM] = true;

    if (!isDesignatedContactComplete(contact, CONTACT_PARAM)) {
      setValidationError(true);
      return;
    }

    onSubmit(contact);
    setDialog(false);
  };

  const handleSelectPrevContact = person => {
    const newContact = person;
    setViewHistory(PREV_ADDED_VIEW);
    setContactView(newContact);
    setSlideView(FORM_VIEW);
  };

  const handleAddNew = () => {
    setViewHistory(PREV_ADDED_VIEW);
    setSlideView(FORM_VIEW);
  };

  const renderPreviouslyAdded = () => (
    <>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant='body1'>
            Select an individual previously entered, or Add a New OTCIQ User.
          </Typography>
          <PreviousAdded
            listTitle='List of Individuals Previously Entered'
            items={prevAddedList}
            handleSelect={handleSelectPrevContact} />
          <Button
            fullWidth
            onClick={handleAddNew}>
            Add New OTCIQ User
          </Button>
        </Box>
      </DialogContent>
      <Controls
        cancelText='Cancel'
        showSubmit={false}
        onCancelClick={() => setDialog(false)} />
    </>
  );

  const dialogClose = () => {
    setDialog(false);
  };

  const renderAddNew = () => {
    const isPresetAffiliation = affilations.some(item => item.value === contactView?.title1);
    const showAffiliationInput = !isExistingContact && (contactView?.title1 === OTHER_PLACEHOLDER || (contactView?.title1 && !isPresetAffiliation));
    const affiliationMissing = validationError && !isExistingContact && !contactView?.title1;
    const emailInvalid = validationError && !!(contactView?.email && !isEmail(contactView?.email));
    const emailMissing = validationError && !contactView?.email;
    const phoneBlank = validationError && (!contactView?.phone || !contactView?.phoneCountryCode);
    const phoneInvalid = validationError && !!(contactView?.phone && contactView?.phoneCountryCode && !isPhoneWithCountry(contactView?.phone, contactView?.phoneCountryCode));
    const phoneErrorText = phoneBlank ? errors.INCOMPLETE_DATA_FIELD : phoneInvalid ? errors.INVALID_PHONE : '';

    return <>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <PrivateInformation />
          {!isExistingContact && <>
            <Typography variant='body1'>
              Enter title and name of OTCIQ User.
            </Typography>
            <Box display='flex' flexDirection='column' gap={2}>
              <TextField
                label='First Name'
                value={contactView?.firstName || ''}
                readOnly={isReadOnly}
                placeholder='Enter text here'
                showClearButton
                maxLength={75}
                error={(validationError || isReadOnly) && !contactView?.firstName}
                helperText={(validationError || isReadOnly) && !contactView?.firstName ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('firstName', e.target.value)}
              />
              <TextField
                label='Middle Name'
                value={contactView?.middleName || ''}
                readOnly={isReadOnly}
                placeholder='Enter text here'
                maxLength={75}
                showClearButton
                onChange={e => onFieldChange('middleName', e.target.value)}
              />
              <TextField
                label='Last Name'
                value={contactView?.lastName || ''}
                readOnly={isReadOnly}
                placeholder='Enter text here'
                maxLength={75}
                showClearButton
                error={(validationError || isReadOnly) && !contactView?.lastName}
                helperText={(validationError || isReadOnly) && !contactView?.lastName ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('lastName', e.target.value)}
              />
            </Box>
          </>}
          <Typography variant='body1'>
            Enter phone number and email for OTCIQ User.
          </Typography>
          <Box display='flex' flexDirection='column' gap={2}>
            <Box>
              <Label startIcon={<Lock size='16px' color='var(--otc-green)' />}>
                Phone Number
              </Label>
              <PhoneInputField
                value={contactView?.phone || ''}
                country={contactView?.phoneCountryCode?.toLowerCase()}
                error={phoneBlank || phoneInvalid}
                errorText={phoneErrorText}
                onChange={onPhoneValueChanged}
              />
            </Box>
            <Box>
              <Label startIcon={<Lock size='16px' color='var(--otc-green)' />}>
                Email
              </Label>
              <TextField
                value={contactView?.email || ''}
                readOnly={isReadOnly}
                placeholder='Enter text here'
                maxLength={75}
                showClearButton
                error={emailMissing || emailInvalid}
                helperText={emailMissing ? errors.INCOMPLETE_DATA_FIELD : emailInvalid ? errors.INVALID_EMAIL : ''}
                onChange={e => onFieldChange('email', e.target.value)}
              />
            </Box>
            {!isExistingContact && <>
              <SelectField
                label='Company Affiliation'
                value={contactView?.title1 ? (isPresetAffiliation ? contactView.title1 : OTHER_PLACEHOLDER) : ''}
                options={affilations}
                readOnly={isReadOnly}
                error={affiliationMissing}
                helperText={affiliationMissing ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onAffiliationChange(e.target.value)}
              />
              {showAffiliationInput && <TextField
                label='Enter other affiliation'
                value={contactView?.title1 === OTHER_PLACEHOLDER ? '' : (contactView?.title1 || '')}
                readOnly={isReadOnly}
                placeholder='Enter text here'
                showClearButton
                maxLength={75}
                error={(validationError || isReadOnly) && contactView?.title1 === OTHER_PLACEHOLDER}
                helperText={(validationError || isReadOnly) && contactView?.title1 === OTHER_PLACEHOLDER ? errors.INCOMPLETE_DATA_FIELD : ''}
                onChange={e => onFieldChange('title1', e.target.value)}
              />}
            </>}
          </Box>
          {showBGCheckLabel && <InfoPanel title='OTC Markets may require a Background Check Authorization Form for new authorized IQ users.' />}
          {validationError && <InfoPanel isWarning title={errors.INCOMPLETE_DATA} />}
        </Box>
      </DialogContent>
      {!isReadOnly && <Controls
        cancelText='Cancel'
        submitText={isAdd ? 'Add' : 'Update'}
        onCancelClick={handleCancelView}
        onSubmitClick={handleSubmitPerson}
      />}
    </>;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {contactTotal < 1 && <InfoPanel isWarning title='One OTCIQ User is required.' />}
      <Typography variant='body1'>
        Designate up to 3 contacts to access OTCIQ, our secure web portal used to publish disclosure and news, update company profile and view trade data.
      </Typography>
      <Box>
        <Box display='flex' justifyContent='space-between' alignItems='center'>
          <SectionTitle noUnderline>OTCIQ Users</SectionTitle>
          {!isReadOnly && <Button variant='text' onClick={() => setDialog(true)}>
            Add OTCIQ User
          </Button>}
        </Box>

        {authorizedUserList.length === 0 && <Typography variant='body2'>
          No OTCIQ Users have been added yet.
        </Typography>}

        <EntityLabelGroup>
          {authorizedUserList.map((person, index) => {
            const complete = validateComplete(person);
            const customRemove = isCustomRemove(person);
            const confirmRemoveText = getLastAuthUser(person, peopleList) ? removeWarningText : entities.CONFIRM_REMOVE_PERSON;

            return <EntityLabel
              key={`auth-${person.id || index}`}
              title={personEntityTitle(person, false)}
              subtitle={isReadOnly ? 'View' : complete ? 'Complete' : 'Enter Information'}
              icon={complete ? <UserCheck /> : <User />}
              actionIcon={<Trash2 />}
              actionConfirmText={confirmRemoveText}
              isComplete={complete}
              isWarning={!complete}
              onEntityClick={() => handleEdit(person)}
              onActionClick={!isReadOnly ? () => customRemove ? handleRemoveCustom(person) : handleRemove(person) : undefined} />;
          })}
        </EntityLabelGroup>
      </Box>
      <Dialog open={dialog} onClose={dialogClose}>
        <DialogTitle title={dialogTitle} onClose={dialogClose} />
        {slideView === PREV_ADDED_VIEW && renderPreviouslyAdded()}
        {slideView === FORM_VIEW && renderAddNew()}
        {isRemoveView && <RemovePerson
          person={{ ...contactView }}
          selectedRemove={CONTACT_PARAM}
          onCancel={handleCancelRemove}
          onRemoveSubmit={onRemoveSubmit} />}
      </Dialog>
    </Box>
  );
};

AuthorizedUsersTab.propTypes = {
  onPersonAdd: PropTypes.func,
  onPersonUpdate: PropTypes.func,
  onPersonRemove: PropTypes.func
};

export default AuthorizedUsersTab;
